import { match } from "ts-pattern";

import type { Assessment } from "../types/assessment.js";
import type { PersonAction } from "../types/personActions.js";
import type { UserId } from "../types/user.js";

/**
 * Whether a user may delete an assessment, judged against the status it is in.
 *
 * Its owner deletes it only while it is in progress: once they have submitted
 * it, the work is the approver's to decide and no longer theirs to take back.
 * Whoever runs the case, `assign` over the person as `personActionsOf` conveys
 * it, deletes it at any point before it is finished, because one that was
 * started in error or is stuck blocks the next definitive assessment until
 * somebody clears it. A finished assessment is part of the person's assessment
 * history and is never deleted.
 *
 * One statement for the server's gate and the page's offer, so a page never
 * offers a delete the server refuses.
 */
export const mayDeleteAssessment = ({
  actions,
  userId,
  assessment,
}: {
  actions: readonly PersonAction[];
  userId: UserId;
  assessment: Pick<Assessment, "ownerUserId" | "status">;
}): boolean => {
  const owns = assessment.ownerUserId === userId;
  const runsTheCase = actions.includes("assign");

  return (
    actions.includes("read") &&
    match(assessment.status)
      .with("open", () => owns || runsTheCase)
      .with("submitted", "returned", () => runsTheCase)
      .with("approved", "completed", () => false)
      .exhaustive()
  );
};
